
import React, { useState } from "react";
import { ArrowLeft, Calculator as CalcIcon, Cigarette, Euro, TrendingUp, Flame, BookText } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { motion } from "framer-motion";

const commonBrands = [
  { name: "Marlboro Red", price: 6.00 },
  { name: "Marlboro Gold", price: 6.00 },
  { name: "Camel Blue", price: 5.80 },
  { name: "Lucky Strike", price: 5.70 },
  { name: "Diana", price: 4.80 },
  { name: "MS", price: 4.50 },
  { name: "Chesterfield", price: 5.50 },
  { name: "Philip Morris", price: 5.90 },
  { name: "Pall Mall", price: 5.40 },
  { name: "Tabacco", price: 0 },
  { name: "Personalizzato", price: 0 }
];

const timePeriods = [
  { label: "1 Mese", days: 30, years: 0, color: "bg-blue-500" },
  { label: "1 Anno", days: 365, years: 1, color: "bg-green-500" },
  { label: "5 Anni", days: 1825, years: 5, color: "bg-yellow-500" },
  { label: "10 Anni", days: 3650, years: 10, color: "bg-orange-500" },
  { label: "20 Anni", days: 7300, years: 20, color: "bg-red-500" },
  { label: "30 Anni", days: 10950, years: 30, color: "bg-purple-500" },
  { label: "40 Anni", days: 14600, years: 40, color: "bg-pink-500" }
];

export default function CalculatorPage() {
  const navigate = useNavigate();
  const [selectedBrand, setSelectedBrand] = useState("");
  const [customPrice, setCustomPrice] = useState("");
  const [dailyCigarettes, setDailyCigarettes] = useState("");
  const [lightersPerWeek, setLightersPerWeek] = useState("1");
  const [cigarettesPerPack, setCigarettesPerPack] = useState("20");
  
  // Nuovi stati per il tabacco
  const [tobaccoPacksPerWeek, setTobaccoPacksPerWeek] = useState("");
  const [tobaccoPackPrice, setTobaccoPackPrice] = useState("");
  const [paperPackPrice, setPaperPackPrice] = useState("1.00");
  const [paperPacksPerWeek, setPaperPacksPerWeek] = useState("1");
  const [filterPackPrice, setFilterPackPrice] = useState("2.00");
  const [filterPacksPerWeek, setFilterPacksPerWeek] = useState("1");
  
  const [results, setResults] = useState(null);

  const isTabacco = selectedBrand === "Tabacco";

  const calculateSavingsWithInflation = (basePrice, dailyCigarettesNum, cigarettesPerPackNum, lightersPerWeekNum, years, tobaccoData = null) => {
    // Calcolo per 1 mese (senza inflazione)
    if (years === 0) {
      const days = 30;
      const weeks = 4;
      const lighterSavings = lightersPerWeekNum * weeks * 1.00;
      let result = { lighterSavings, avgPrice: basePrice };

      if (tobaccoData) {
        const tobaccoCost = tobaccoData.packsPerWeek * tobaccoData.packPrice * weeks;
        const paperCost = tobaccoData.paperPacksPerWeek * tobaccoData.paperPackPrice * weeks;
        const filterCost = tobaccoData.filterPacksPerWeek * tobaccoData.filterPackPrice * weeks;
        const accessoriesSavings = paperCost + filterCost;
        
        result.totalCigarettes = tobaccoData.packsPerWeek * weeks * 100; // Stima 100 sigarette per pacco
        result.tobaccoSavings = tobaccoCost;
        result.accessoriesSavings = accessoriesSavings;
        result.totalSavings = tobaccoCost + accessoriesSavings + lighterSavings;
      } else {
        const pricePerCigarette = basePrice / cigarettesPerPackNum;
        const totalCigarettes = dailyCigarettesNum * days;
        const cigaretteSavings = totalCigarettes * pricePerCigarette;
        
        result.totalCigarettes = totalCigarettes;
        result.cigaretteSavings = cigaretteSavings;
        result.totalSavings = cigaretteSavings + lighterSavings;
      }
      return result;
    }

    let totalCigaretteSavings = 0;
    let totalTobaccoSavings = 0;
    let totalAccessoriesSavings = 0;
    let totalLighterSavings = 0;
    let totalCigarettes = 0;
    let currentCigarettePrice = basePrice;
    let currentLighterPrice = 1.00;
    let currentTobaccoPrice = tobaccoData?.packPrice || 0;
    let currentPaperPrice = tobaccoData?.paperPackPrice || 0;
    let currentFilterPrice = tobaccoData?.filterPackPrice || 0;

    // Calcolo anno per anno
    for (let year = 1; year <= years; year++) {
      if (tobaccoData) {
        // Calcolo tabacco per l'anno corrente
        const tobaccoWeeksYear = 52;
        const tobaccoCost = tobaccoData.packsPerWeek * currentTobaccoPrice * tobaccoWeeksYear;
        const paperCost = tobaccoData.paperPacksPerWeek * currentPaperPrice * tobaccoWeeksYear;
        const filterCost = tobaccoData.filterPacksPerWeek * currentFilterPrice * tobaccoWeeksYear;
        totalTobaccoSavings += tobaccoCost;
        totalAccessoriesSavings += paperCost + filterCost;
        totalCigarettes += tobaccoData.packsPerWeek * tobaccoWeeksYear * 100; // Stima 100 sigarette
      } else {
        // Calcolo sigarette per l'anno corrente
        const cigarettesThisYear = dailyCigarettesNum * 365;
        const pricePerCigarette = currentCigarettePrice / cigarettesPerPackNum;
        totalCigaretteSavings += cigarettesThisYear * pricePerCigarette;
        totalCigarettes += cigarettesThisYear;
      }

      // Calcolo accendini per l'anno corrente
      const lightersThisYear = lightersPerWeekNum * 52;
      totalLighterSavings += lightersThisYear * currentLighterPrice;
      
      // L'aumento del 3% si applica solo dalla fine del primo anno in poi
      if (year >= 1) {
        currentCigarettePrice *= 1.03;
        currentLighterPrice *= 1.03;
        if (tobaccoData) {
          currentTobaccoPrice *= 1.03;
          currentPaperPrice *= 1.03;
          currentFilterPrice *= 1.03;
        }
      }
    }

    const totalSavings = (tobaccoData ? totalTobaccoSavings + totalAccessoriesSavings : totalCigaretteSavings) + totalLighterSavings;

    return {
      totalCigarettes,
      cigaretteSavings: totalCigaretteSavings,
      tobaccoSavings: totalTobaccoSavings,
      accessoriesSavings: totalAccessoriesSavings,
      lighterSavings: totalLighterSavings,
      totalSavings: totalSavings,
      avgPrice: tobaccoData ? currentTobaccoPrice * Math.pow(1.03, years / 2) : basePrice * Math.pow(1.03, years / 2)
    };
  };

  const calculateSavings = () => {
    if (!selectedBrand || !lightersPerWeek) return;
    
    if (isTabacco) {
      if (!tobaccoPacksPerWeek || !tobaccoPackPrice || !paperPackPrice || !paperPacksPerWeek || !filterPackPrice || !filterPacksPerWeek) return;
    } else {
      if (!dailyCigarettes) return;
      if (selectedBrand === "Personalizzato" && !customPrice) return;
    }

    let pricePerPack = 0;
    let tobaccoData = null;
    
    if (isTabacco) {
      tobaccoData = {
        packsPerWeek: parseFloat(tobaccoPacksPerWeek),
        packPrice: parseFloat(tobaccoPackPrice),
        paperPacksPerWeek: parseFloat(paperPacksPerWeek),
        paperPackPrice: parseFloat(paperPackPrice),
        filterPacksPerWeek: parseFloat(filterPacksPerWeek),
        filterPackPrice: parseFloat(filterPackPrice)
      };
      pricePerPack = tobaccoData.packPrice;
    } else {
      if (selectedBrand === "Personalizzato") {
        pricePerPack = parseFloat(customPrice);
      } else {
        const brand = commonBrands.find(b => b.name === selectedBrand);
        pricePerPack = brand.price;
      }
    }

    const cigarettesPerPackNum = parseInt(cigarettesPerPack);
    const dailyCigarettesNum = isTabacco ? 0 : parseInt(dailyCigarettes);
    const lightersPerWeekNum = parseInt(lightersPerWeek);

    const calculations = timePeriods.map(period => {
      const calculation = calculateSavingsWithInflation(
        pricePerPack, 
        dailyCigarettesNum, 
        cigarettesPerPackNum,
        lightersPerWeekNum,
        period.years,
        tobaccoData
      );
      
      const packsAvoided = Math.floor(calculation.totalCigarettes / (isTabacco ? 100 : cigarettesPerPackNum)); // Updated 50 to 100 for tobacco

      return {
        ...period,
        ...calculation,
        packsAvoided,
      };
    });

    setResults(calculations);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('it-IT').format(Math.round(num));
  };

  const formatCurrency = (num) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };

  return (
    <div className="min-h-screen p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <Button variant="outline" onClick={() => navigate(createPageUrl("Dashboard"))} className="mb-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Torna alla Bacheca
          </Button>
          
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Calcolatore <span className="bg-gradient-to-r from-green-600 to-blue-600 bg-clip-text text-transparent">Risparmi</span>
          </h1>
          <p className="text-lg text-gray-600">
            Scopri quante sigarette hai evitato e quanto hai risparmiato smettendo di fumare
          </p>
          <p className="text-sm text-blue-600 mt-2">
            💡 <strong>Nota:</strong> I calcoli includono l'aumento annuale del 3% del prezzo delle sigarette e degli accendini.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Input Form */}
          <div className="lg:col-span-1">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Card className="shadow-xl">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CalcIcon className="w-6 h-6" />
                    I Tuoi Dati
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div>
                    <Label>Tipo di fumo</Label>
                    <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                      <SelectTrigger>
                        <SelectValue placeholder="Scegli il tipo" />
                      </SelectTrigger>
                      <SelectContent>
                        {commonBrands.map(brand => (
                          <SelectItem key={brand.name} value={brand.name}>
                            {brand.name} {brand.price > 0 && `(€${brand.price.toFixed(2)})`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Campi per Sigarette Normali */}
                  {!isTabacco && (
                    <>
                      {selectedBrand === "Personalizzato" && (
                        <div>
                          <Label>Prezzo per pacchetto (€)</Label>
                          <Input
                            type="number"
                            step="0.10"
                            value={customPrice}
                            onChange={(e) => setCustomPrice(e.target.value)}
                            placeholder="5.50"
                          />
                        </div>
                      )}

                      <div>
                        <Label>Sigarette per pacchetto</Label>
                        <Select value={cigarettesPerPack} onValueChange={setCigarettesPerPack}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10 sigarette</SelectItem>
                            <SelectItem value="20">20 sigarette</SelectItem>
                            <SelectItem value="25">25 sigarette</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div>
                        <Label>Sigarette al giorno (prima di smettere)</Label>
                        <Input
                          type="number"
                          value={dailyCigarettes}
                          onChange={(e) => setDailyCigarettes(e.target.value)}
                          placeholder="20"
                        />
                      </div>
                    </>
                  )}

                  {/* Campi per Tabacco */}
                  {isTabacco && (
                    <>
                      <div>
                        <Label>Pacchi di tabacco alla settimana</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={tobaccoPacksPerWeek}
                          onChange={(e) => setTobaccoPacksPerWeek(e.target.value)}
                          placeholder="2"
                        />
                      </div>
                      <div>
                        <Label>Prezzo per pacco di tabacco (€)</Label>
                        <Input
                          type="number"
                          step="0.10"
                          value={tobaccoPackPrice}
                          onChange={(e) => setTobaccoPackPrice(e.target.value)}
                          placeholder="15.00"
                        />
                      </div>
                      <div>
                        <Label>Prezzo pacco cartine (€)</Label>
                        <Input
                          type="number"
                          step="0.10"
                          value={paperPackPrice}
                          onChange={(e) => setPaperPackPrice(e.target.value)}
                          placeholder="1.00"
                        />
                      </div>
                      <div>
                        <Label>Pacchi cartine alla settimana</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={paperPacksPerWeek}
                          onChange={(e) => setPaperPacksPerWeek(e.target.value)}
                          placeholder="1"
                        />
                      </div>
                      <div>
                        <Label>Prezzo pacco filtri (€)</Label>
                        <Input
                          type="number"
                          step="0.10"
                          value={filterPackPrice}
                          onChange={(e) => setFilterPackPrice(e.target.value)}
                          placeholder="2.00"
                        />
                      </div>
                      <div>
                        <Label>Pacchi filtri alla settimana</Label>
                        <Input
                          type="number"
                          step="0.5"
                          value={filterPacksPerWeek}
                          onChange={(e) => setFilterPacksPerWeek(e.target.value)}
                          placeholder="1"
                        />
                      </div>
                    </>
                  )}
                  
                  <div>
                    <Label>Accendini a settimana (circa)</Label>
                    <Input
                      type="number"
                      value={lightersPerWeek}
                      onChange={(e) => setLightersPerWeek(e.target.value)}
                      placeholder="1"
                    />
                  </div>

                  <Button 
                    onClick={calculateSavings} 
                    className="w-full bg-gradient-to-r from-green-500 to-blue-600"
                    disabled={
                      !selectedBrand || 
                      !lightersPerWeek || 
                      (!isTabacco && (!dailyCigarettes || (selectedBrand === "Personalizzato" && !customPrice))) ||
                      (isTabacco && (!tobaccoPacksPerWeek || !tobaccoPackPrice || !paperPackPrice || !paperPacksPerWeek || !filterPackPrice || !filterPacksPerWeek))
                    }
                  >
                    <CalcIcon className="w-4 h-4 mr-2" />
                    Calcola Risparmi
                  </Button>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Results */}
          <div className="lg:col-span-2">
            {results ? (
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4 }}
                className="space-y-6"
              >
                <h2 className="text-2xl font-bold text-gray-800 mb-4">I Tuoi Risparmi</h2>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {results.map((result, index) => (
                    <motion.div
                      key={result.label}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 * index }}
                    >
                      <Card className="shadow-lg hover:shadow-xl transition-shadow duration-300">
                        <CardHeader className="pb-3">
                          <CardTitle className="flex items-center justify-between">
                            <span className="text-lg">{result.label}</span>
                            <div className={`w-4 h-4 rounded-full ${result.color}`}></div>
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-4">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Cigarette className="w-5 h-5 text-gray-500" />
                              <span className="text-sm text-gray-600">Sigarette evitate</span>
                            </div>
                            <span className="font-bold text-lg">{formatNumber(result.totalCigarettes)}</span>
                          </div>
                          
                          <div className="p-3 bg-green-50 rounded-lg space-y-2">
                            {isTabacco ? (
                              <>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <Cigarette className="w-5 h-5 text-green-700" />
                                    <span className="text-sm text-gray-600">Risparmio tabacco</span>
                                  </div>
                                  <span className="font-semibold text-md text-green-700">{formatCurrency(result.tobaccoSavings)}</span>
                                </div>
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <BookText className="w-5 h-5 text-teal-700" />
                                    <span className="text-sm text-gray-600">Risparmio cartine e filtri</span>
                                  </div>
                                  <span className="font-semibold text-md text-teal-700">{formatCurrency(result.accessoriesSavings)}</span>
                                </div>
                              </>
                            ) : (
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <Cigarette className="w-5 h-5 text-green-700" />
                                  <span className="text-sm text-gray-600">Risparmio sigarette</span>
                                </div>
                                <span className="font-semibold text-md text-green-700">{formatCurrency(result.cigaretteSavings)}</span>
                              </div>
                            )}

                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Flame className="w-5 h-5 text-orange-600" />
                                <span className="text-sm text-gray-600">Risparmio accendini</span>
                              </div>
                              <span className="font-semibold text-md text-orange-600">{formatCurrency(result.lighterSavings)}</span>
                            </div>
                            <div className="flex items-center justify-between pt-2 border-t">
                              <div className="flex items-center gap-2">
                                <Euro className="w-5 h-5 text-green-600" />
                                <span className="text-sm font-bold text-gray-700">Risparmio TOTALE</span>
                              </div>
                              <span className="font-bold text-lg text-green-600">{formatCurrency(result.totalSavings)}</span>
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <TrendingUp className="w-5 h-5 text-blue-600" />
                              <span className="text-sm text-gray-600">{isTabacco ? 'Pacchi tabacco' : 'Pacchetti'} evitati</span>
                            </div>
                            <span className="font-bold text-lg text-blue-600">{formatNumber(result.packsAvoided)}</span>
                          </div>

                          {result.years > 0 && (
                            <div className="pt-2 border-t border-gray-100">
                              <p className="text-xs text-gray-500">
                                Prezzo medio periodo: {formatCurrency(result.avgPrice)}/{isTabacco ? 'pacco tabacco' : 'pacchetto'}
                              </p>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  ))}
                </div>

                {/* Summary Card */}
                <Card className="bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-200 shadow-xl">
                  <CardHeader>
                    <CardTitle className="text-center text-2xl text-green-800">
                      🎉 Congratulazioni per la tua scelta! 
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="text-center">
                    <p className="text-lg text-gray-700">
                      In 40 anni, considerando l'aumento del 3% annuale, avresti speso <span className="font-bold text-2xl text-red-600">{formatCurrency(results[6]?.totalSavings)}</span> tra {isTabacco ? 'tabacco, cartine, filtri' : 'sigarette'} e accendini.
                    </p>
                    <p className="text-md text-gray-600 mt-2">
                      Pensa a tutto quello che puoi fare con questi soldi! 🌟
                    </p>
                    <p className="text-sm text-blue-600 mt-3">
                      💡 Il calcolo include l'inflazione annuale del 3% sui prezzi a partire dal secondo anno.
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <div className="flex items-center justify-center h-64 text-gray-500">
                <div className="text-center">
                  <CalcIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Inserisci i tuoi dati per vedere i risultati</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
